/**
 * Pure helpers for building the Process_Requests sheet rows written by
 * updateSheets(). Extracted so the Email Sent preservation logic can be
 * unit tested without a live Google Sheets connection.
 */

import { getRequestKey } from './requestKey';

/**
 * Builds a map of request key -> Email Sent value from the sheet's current
 * raw values, so writes can preserve whatever Apps Script's polling already
 * wrote (which may be newer than what the app last loaded into state).
 *
 * Rows are keyed by their Request ID when the column exists and the row has
 * one, falling back to Timestamp for pre-migration rows — mirroring
 * getRequestKey() so map keys and lookups always derive from the same data.
 */
export const buildEmailSentMap = (allCurrentValues, headers) => {
  const emailSentIdx = headers.indexOf('Email Sent');
  const timestampIdx = headers.indexOf('Timestamp');
  const requestIdIdx = headers.indexOf('Request ID');
  const map = {};
  // Need the Email Sent column plus at least one key column. Don't require
  // Timestamp specifically — a fully migrated sheet may key purely on Request ID.
  if (emailSentIdx < 0 || (timestampIdx < 0 && requestIdIdx < 0)) return map;

  for (let i = 1; i < allCurrentValues.length; i++) {
    const row = allCurrentValues[i];
    const key =
      (requestIdIdx >= 0 && row[requestIdIdx]) ||
      (timestampIdx >= 0 && row[timestampIdx]) ||
      '';
    const es = row[emailSentIdx];
    if (key && es) map[key] = es;
  }
  return map;
};

/**
 * Builds the 2D rows array (header row + data rows) to write back to
 * Process_Requests, applying the Email Sent preservation/sentinel rules:
 *  - 'RESEND' sentinel clears the cell (user asked to resend)
 *  - a real value already in React state wins
 *  - otherwise, preserve whatever's already in the sheet (Apps Script wrote it)
 */
export const buildRequestsRows = (updatedRequests, headers, sheetEmailSentMap) => [
  headers,
  ...updatedRequests.map((req) =>
    headers.map((h) => {
      if (h === 'Email Sent') {
        const val = req['Email Sent'];
        if (val === 'RESEND') return '';
        if (val && val !== '') return val;
        return sheetEmailSentMap[getRequestKey(req)] || '';
      }
      return req[h] || '';
    })
  ),
];
